import {
    sanitizeErrorMessage,
    type AutoPopulatedEventFields,
    type ErrorEvent as LogErrorEvent,
} from '@fiji/common/src/services/logger';
import { logger } from './logger';

/**
 * Fields the caller of `logger.error` supplies for a category `'error'`
 * event — every field the {@link Logger} auto-populates is stripped so
 * the shape matches `ErrorEvent`'s excess-property check.
 */
type ErrorEventInput = Omit<LogErrorEvent, AutoPopulatedEventFields>;

/**
 * Best-effort resolution of the currently rendered page for error context.
 * Kept synchronous and side-effect-free so it can run from the earliest
 * `window.onerror` handler, before React has mounted.
 */
const resolveContextPageName = (): string =>
    typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : 'unknown';

/**
 * Namespaced flag hung off `globalThis` so it survives Webpack HMR module
 * re-execution. A plain module-scoped `let` resets to `false` on every hot
 * reload, which — combined with `addEventListener` — would leak a fresh
 * listener on each save while the previous one stays wired, emitting each
 * event twice, three times, etc. The symbol key is unique per Realm, so
 * two dev bundles served side-by-side (e.g. legacy iframes) don't collide.
 */
const INSTALLED_KEY = Symbol.for('@fiji/web/globalErrorHandlersInstalled');
type GlobalWithFlag = typeof globalThis & { [INSTALLED_KEY]?: boolean };

/**
 * Wire `window.onerror` and `window.onunhandledrejection` to emit
 * {@link LogErrorEvent}s via the shared web logger.
 *
 * Confluence spec MUSTs covered:
 * - "Any unhandled error"
 * - "Errors — sanitized, no PII"
 * - Log level `ERROR`, category `error`.
 *
 * Uses `addEventListener` (not assignment) so other libraries that also
 * install `onerror` handlers keep working. Once the event is captured and
 * forwarded through the app logger, the default browser-console emission is
 * suppressed to avoid duplicating the same error locally.
 */
export const installGlobalErrorHandlers = (): void => {
    if (typeof window === 'undefined') {
        return;
    }
    const g = globalThis as GlobalWithFlag;
    if (g[INSTALLED_KEY]) {
        return;
    }
    g[INSTALLED_KEY] = true;

    window.addEventListener('error', (event: ErrorEvent): void => {
        // `ErrorEvent.error` is the thrown value when available; some scripts
        // (cross-origin, older browsers) only expose `event.message`. Fall
        // through to that so we still emit something useful.
        const err: unknown = event.error ?? event.message;
        const payload: ErrorEventInput = {
            category: 'error',
            eventType: 'js_error',
            errorType: 'JS_RUNTIME',
            errorMessage: sanitizeErrorMessage(err),
            stackTrace: event.error instanceof Error ? event.error.stack : undefined,
            contextPageName: resolveContextPageName(),
        };
        logger.error(payload);
        event.preventDefault();
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
        const reason: unknown = event.reason;
        const payload: ErrorEventInput = {
            category: 'error',
            eventType: 'js_error',
            errorType: 'UNHANDLED_REJECTION',
            errorMessage: sanitizeErrorMessage(reason),
            stackTrace: reason instanceof Error ? reason.stack : undefined,
            contextPageName: resolveContextPageName(),
        };
        logger.error(payload);
        event.preventDefault();
    });
};
